type ConnectionHint = { saveData?: boolean; effectiveType?: string }
type DeviceHints = Navigator & {
  connection?: ConnectionHint
  /** GB, rounded (Chrome only); 0.25, 0.5, 1, 2, 4, 8 */
  deviceMemory?: number
}

// 3g keeps one slide ahead: it starts only after the active clip plays, so it
// does not slow the start, and it turns a ~6 s swipe into ~0.5 s there
const SLOW_CONNECTIONS = ['slow-2g', '2g']
// slides loaded ahead on a fast link. Two are enough: each clip that starts
// moves the window on, so three quick swipes still land on ready clips
// (measured: a third one ahead only adds a download)
const AHEAD_FAST = 2
const AHEAD_SLOW = 1
// watched slides kept behind on a fast link: no download (they are already
// loaded) and three quick swipes back start at once. Each is still a live
// player: on a slow link (a slow phone, measured) three cost ~2 s on a swipe
const BEHIND_FAST = 3
const BEHIND_SLOW = 1
// on a phone with this much memory or less, every live player counts
const LOW_MEMORY_GB = 2

export type ReelsNetwork = {
  ahead: number
  behind: number
  preloadAdMedia: boolean
}

/**
 * ahead: how many slides load ahead of the active one (0 on 2g or with data
 * saver, 1 on 3g, an unknown link or a low-memory phone, 2 otherwise).
 * behind: watched slides kept loaded (3 on 4g with enough memory, else 1).
 * preloadAdMedia: also download the next ad's file, only on a 4g link.
 * Safari has neither hint, so it is treated as a 4g phone with enough memory.
 */
export const readNetwork = (nav: DeviceHints = navigator): ReelsNetwork => {
  const { connection, deviceMemory } = nav
  const lowMemory =
    typeof deviceMemory === 'number' && deviceMemory <= LOW_MEMORY_GB
  const effectiveType = connection ? connection.effectiveType ?? '' : '4g'
  if (connection?.saveData || SLOW_CONNECTIONS.includes(effectiveType)) {
    return { ahead: 0, behind: BEHIND_SLOW, preloadAdMedia: false }
  }
  const fast = effectiveType === '4g'
  const roomy = fast && !lowMemory
  return {
    ahead: roomy ? AHEAD_FAST : AHEAD_SLOW,
    behind: roomy ? BEHIND_FAST : BEHIND_SLOW,
    preloadAdMedia: fast,
  }
}
